import { useState, useEffect, useMemo } from 'react';
import { bannerAdPositions } from '@Components/Utilities/SandhillsConstants';
import { sandhillsRequest, getBannerAd, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter } from '@Components/Utilities/SandhillsUtility';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import Paging from '@Components/MuiWrappers/Paging';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import TranslationText from '@Components/Utilities/TranslationText';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import ready from 'document-ready';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';
import debounce from 'lodash.debounce';

const IndustryWebsitesView = (props) => {

    const topBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Top);
    const constants = { page: 'Page' };
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [searchData, setSearchData] = useState({
        CurrentPage: props.PaginationModel.CurrentPage,
        Websites: props.Websites,
        TotalPages: props.PaginationModel.TotalPages,
        PageTitle: props.PageTitle,
        SearchFields: props.searchFields,
        SearchBaseURL: props.SearchBaseURL,
        IsDefaultSearch: props.IsDefaultSearch,
        CategoryInfo: props.CategoryInformation
    });
    const currentSite = useRecoilValue(atoms.currentWebsite);

    useEffect(() => {
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
        });

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    const updatePageNumber = (page) => {
        let searchURL = searchData.SearchBaseURL;
        if (props.CategoryIDs.length > 0) {
            searchURL = insertURLParameter(searchURL, 'CategoryID', props.CategoryIDs);
        }

        if (page > 1) {
            searchURL = insertURLParameter(searchURL, constants.page, page);
        } else {
            searchURL = removeURLParameter(searchURL, constants.page);
        }

        setSearchData(previousState => {
            return {
                ...previousState,
                SearchBaseURL: searchURL,
                CurrentPage: page
            };
        });

        sandhillsRequest({
            url: searchURL,
            errorHandling: {
                message: 'An error occured while searching for listings.',
                useConsole: false,
                useAlert: true
            }
        }).then((data) => {
            handleResponse(data);
        });

        window.scrollTo(0, 0);
    };

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName === pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchData.SearchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (latestFieldValue.fieldName === field.FieldName && field.Type === 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchData(previousState => {
                    return { ...previousState, SearchFields: fields };
                });
                break;
            }
        }

        return pendingChanges;
    };

    const runSearch = (keepPage, latestFieldValue) => {
        let pendingChanges = [];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        if (window.innerWidth <= 785) {
            window.scrollTo(0, 0);
        }

        let searchUrl = buildUrl(keepPage, pendingChanges);

        sandhillsRequest({
            url: searchUrl,
            errorHandling: {
                message: 'An error occured while searching for listings. Please try again.',
                useConsole: false,
                useAlert: true
            }
        }).then((data) => {
            handleResponse(data);
        });
    };

    const clearAll = () => {
        window.scrollTo(0, 0);
        window.location = props.ClearAllURL;
    };

    const buildUrl = (keepPage, pendingChanges) => {
        let searchUrl = searchData.SearchBaseURL;
        pendingChanges.forEach(field => {
            if (field.value) {
                searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                if (field.searchType) {
                    searchUrl = insertURLParameter(searchUrl, constants.searchType, field.searchType);
                }
            } else {
                searchUrl = removeURLParameter(searchUrl, field.fieldName);
            }
        });

        searchUrl = removeURLParameter(searchUrl, constants.page);
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, constants.page, searchData.CurrentPage);
        }

        return searchUrl;
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false, []);

        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = searchData.SearchFields.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && (selectedField.ParentNameForSearch && selectedField.ParentNameForSearch != '')) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.length > 0) {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        sandhillsRequest({
            url: searchUrl,
            errorHandling: {
                message: 'An error occured while searching for listings. Please try again.',
                useConsole: false,
                useAlert: true
            }
        }).then((data) => {
            handleResponse(data);
        });
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const handleResponse = (data) => {
        setSearchData({
            CurrentPage: data.PaginationModel.CurrentPage,
            Websites: data.Websites,
            TotalPages: data.PaginationModel.TotalPages,
            PageTitle: data.PageTitle,
            SearchFields: data.searchFields,
            SearchBaseURL: data.SearchBaseURL,
            IsDefaultSearch: data.IsDefaultSearch,
            CategoryInfo: data.CategoryInformation
        });

        window.history.pushState('', data.MetaTitle, data.SearchURL);
    };

    const handleFailure = () => {
        alert('An error occurred while searching for listings.');
    };

    const toggleMobileFilter = (skipSearch) => {
        let newShowMobileFilters = !showMobileFilters;
        setShowMobileFilters(newShowMobileFilters);
        if (newShowMobileFilters) {
            setMobileSearch(window.innerWidth <= 785);
        } else {
            setMobileSearch(false);
            if (!skipSearch) {
                runSearch(false);
            }
        }
    };

    return (
        <div className='industry-website-content' id='industry-website-content'>
            <CategoryInformation {...searchData.CategoryInfo} />
            <section className='list-top-section'>
                <div className='parts-title-and-breadcrumbs'>
                    {!props.IsHostedSite && <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={<span><TranslationText tKey={props.IndustryTranslationKey} /></span>} />}
                    <h1 className='page-title'>
                        <span className='list-title-text'>{props.PageHeader}</span>
                        <span className='list-listings-count'><span dangerouslySetInnerHTML={{ __html: searchData.PageTitle }}></span></span>
                    </h1>
                </div>
                {topBannerAd &&
                    <DoubleClickAd
                        {...props.DoubleClickAdCollection}
                        position={bannerAdPositions.Top}
                        ad={topBannerAd}
                        contained
                    />}
            </section>
            <section className='bottom-section'>
                <div className='side-container'>
                    <FacetedSearch
                        {...searchData}
                        runSearch={runSearch}
                        clearAll={clearAll}
                        clearFacet={clearFacetDebounced}
                        showMobileFilters={showMobileFilters}
                        toggleMobileFilter={toggleMobileFilter}
                        mobileSearch={mobileSearch}
                    />
                </div>
                <main id='main-content' className='industry-website-listings'>
                    {searchData.Websites.map(item =>
                        <>
                            {props.UseNewLayoutForJobsandIndustries ?   
                                <div key={item.LinkID} className='dealer-directory-listing industry-website-listing logo-format'> 
                                    {item.LogoURL && <LazyLoadImage src={item.LogoURL} className={'listing-logo' + (item.LogoURL.indexOf('clear.gif') > -1 ? ' no-logo' : '')} alt={item.Title} />}
                                    <div className='listing-left'>
                                        <a rel='nofollow' href={item.URL} target='_blank' aria-label={item.Title + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>{item.Title}</a>
                                        <TruncatedDescription description={item.Description} length={200} />  
                                    </div>
                                    <div className='listing-right'>
                                        <a className='inventory-button null' rel='nofollow' target='_blank' href={item.URL} aria-label={translator.translate('sandhills.tradesites.mvc.visit') + " " + translator.translate('sandhills.website') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><TranslationText tKey='sandhills.tradesites.mvc.visit' />&nbsp;<TranslationText tKey='sandhills.website' /></a>
                                    </div>
                                </div>
                                :
                                <div key={item.LinkID} className='dealer-directory-listing industry-website-listing'>
                                    <div className='dealer-listing-row'>
                                        <div className='dealer-contact-info'>
                                            <div className='dealer-title-text'>
                                                <div className='dealer-listing-half'>
                                                    <a rel='nofollow' href={item.URL} target='_blank' aria-label={item.Title + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>{item.Title}</a>
                                                </div>
                                                <div className='dealer-listing-half'>
                                                    {item.LogoURL && <LazyLoadImage src={item.LogoURL} className='industry-logo' alt={item.Title} />}
                                                </div>
                                            </div>
                                            {item.Description}
                                        </div>
                                    </div>
                                    <div className='dealer-listing-row button-container'>
                                        <a className='inventory-button old-layout null' rel='nofollow' target='_blank' href={item.URL} aria-label={translator.translate('sandhills.tradesites.mvc.visit') + " " + translator.translate('sandhills.website') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><TranslationText tKey='sandhills.tradesites.mvc.visit' />&nbsp;<TranslationText tKey='sandhills.website' /></a>
                                    </div>
                                </div>
                            }
                        </>
                    )}
                    {searchData.TotalPages > 1 &&
                        <Paging
                            currentPage={searchData.CurrentPage}
                            totalPages={searchData.TotalPages}
                            onChange={updatePageNumber}
                        />}
                </main>
            </section>
            <FacetedSearchMobileMenu
                filter={{
                    render: true,
                    toggle: toggleMobileFilter
                }}
                flyout={{
                    render: props.UseClassicViewFlyout,
                    url: props.ClassicFlyoutModel.ClassicViewURL,
                    img: props.ClassicFlyoutModel.ClassicViewImageURL
                }}
            />
            {currentSite.IsMobileAppContext &&
                <div className='detail__mobile-padding-bottom' />
            }
        </div>
    );
};

const TruncatedDescription = (props) => {
    const [showMore, setShowMore] = useState(true);      
    return (
        <>
            <p>
                {
                    showMore && props.description.length > props.length ? props.description.slice(0, props.length) + "...  " : props.description
                }
            </p>
            {props.description.length > props.length &&
                <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowMore(!showMore); } }} onClick={() => { setShowMore(!showMore) }}> {showMore ? <TranslationText tKey='sandhills.viewmore' /> : <TranslationText tKey='sandhills.viewless' />}</a>
            }
        </>
    )
}

export default IndustryWebsitesView;
global.IndustryWebsitesView = IndustryWebsitesView;